import React, { useState } from 'react';
import { Button, TextInput, Popover, Tooltip } from '@c2fo/liquidity';
import { XmarkIcon, PlusIcon, QuestionCircleIcon } from '@c2fo/liquidity/icons';
import type { SavedSearch } from '../../hooks/useSavedSearches';

interface SavedSearchesProps {
  searches: SavedSearch[];
  onApply: (search: SavedSearch) => void;
  onDelete: (name: string) => void;
  onSave: (search: SavedSearch) => { success: boolean; error?: string };
  currentFilter?: string;
  currentSortField?: string;
  currentSortDirection?: 'asc' | 'desc';
  isNameTaken: (name: string) => boolean;
  activeSearchName?: string;
}

export const SavedSearches: React.FC<SavedSearchesProps> = ({
  searches,
  onApply,
  onDelete,
  onSave,
  currentFilter,
  currentSortField,
  currentSortDirection,
  isNameTaken,
  activeSearchName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    const trimmedName = searchName.trim();
    if (!trimmedName) {
      setError('Name is required');
      return;
    }

    if (isNameTaken(trimmedName)) {
      setError('A search with this name already exists');
      return;
    }

    const result = onSave({
      name: trimmedName,
      filter: currentFilter,
      sortField: currentSortField,
      sortDirection: currentSortDirection,
    });

    if (result.success) {
      setSearchName('');
      setError(null);
      setIsOpen(false);
    } else {
      setError(result.error ?? 'Failed to save search');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  const isSearchActive = (search: SavedSearch): boolean => {
    return search.name === activeSearchName;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {searches.map((search) => {
        const isActive = isSearchActive(search);
        return (
          <button
            key={search.name}
            type="button"
            onClick={() => onApply(search)}
            className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full border transition-colors ${
              isActive
                ? 'bg-primary-100 hover:bg-primary-200 text-primary-700 border-primary-400'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300'
            }`}
          >
            <span>{search.name}</span>
            {!search.isDefault && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(search.name);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    onDelete(search.name);
                  }
                }}
                className="ml-0.5 p-0.5 rounded-full hover:bg-gray-300 transition-colors"
                aria-label={`Delete ${search.name} search`}
              >
                <XmarkIcon className="h-3 w-3" />
              </span>
            )}
          </button>
        );
      })}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <Button variant="tertiary" size="sm" className="rounded-full px-3 py-1 h-auto">
            <span className="flex items-center gap-1">
              <PlusIcon className="h-3.5 w-3.5" />
              Save Search
            </span>
          </Button>
        </Popover.Trigger>
        <Popover.Content variant="light" className="w-64 !p-0" aria-label="Save search dialog">
          <h3 id="save-search-title" className="text-sm font-medium px-3 py-2 m-0">
            Save Current Search
          </h3>
          <div className="p-3 space-y-3">
            <TextInput
              placeholder="Search name..."
              value={searchName}
              onChange={(e) => {
                setSearchName(e.target.value);
                setError(null);
              }}
              onKeyDown={handleKeyDown}
              size="sm"
              className="w-full"
            />
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  setIsOpen(false);
                  setSearchName('');
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave}>
                Save
              </Button>
            </div>
          </div>
        </Popover.Content>
      </Popover>

      <Tooltip.Provider>
        <Tooltip>
          <Tooltip.Trigger asChild>
            <span className="inline-flex items-center text-gray-400 hover:text-gray-600 cursor-help">
              <QuestionCircleIcon className="h-4 w-4" />
            </span>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content variant="dark" arrow className="max-w-xs">
              <p className="mb-2">
                Save your current filter and sort settings as a named search for quick access later.
              </p>
              <p className="text-amber-300">
                ⚠️ Saved searches are stored in your browser&apos;s local storage. Clearing your browser data will
                remove them.
              </p>
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip>
      </Tooltip.Provider>
    </div>
  );
};
